import React, { useMemo, useState } from "react";
import { Box, Paper, Popover, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { fmtMoney, resolveMpFeeAmount, isMpFeePending } from "../../Passagens/passagemMpFeeUi";
import {
  resolveMpPaymentFromRow,
  formatInstallmentsBreakdownLabel,
  mpPaymentTypeLabel,
} from "../../Passagens/passagemMpPaymentUi";
import { paymentRailLabel } from "./passengerTaxConstants";

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function hasComposition(row) {
  return (
    num(row.valor_tarifa) > 0 ||
    num(row.valor_taxas_embarque) > 0 ||
    num(row.acrescimo_fp) > 0 ||
    num(row.desconto_fp) > 0
  );
}

function resolveContractualFee(row) {
  if (row?.taxa_contratual != null && Number.isFinite(Number(row.taxa_contratual))) {
    return num(row.taxa_contratual);
  }
  const charged = num(row.valor_cobrado);
  const net = row.valor_liquido_cliente;
  if (net != null && Number.isFinite(Number(net))) {
    return Math.max(0, charged - num(net));
  }
  const partner = num(row.taxa_parceiro);
  const mpRaw = resolveMpFeeAmount(row);
  const mp = mpRaw != null ? num(mpRaw) : 0;
  return num(row.taxa_xvision) + partner + mp;
}

function feeParts(row, { tenantView = false } = {}) {
  const partner = num(row.taxa_parceiro);
  const mpRaw = resolveMpFeeAmount(row);
  const mp = mpRaw != null ? num(mpRaw) : 0;
  const mpPending = isMpFeePending(row);
  const xvision = num(row.taxa_xvision);
  const contractual = tenantView ? resolveContractualFee(row) : xvision;
  const surcharge = num(row.sobretaxa);
  const totalFees = partner + mp + xvision + surcharge;
  const charged = num(row.valor_cobrado);
  const net =
    row.valor_liquido_cliente != null && Number.isFinite(Number(row.valor_liquido_cliente))
      ? num(row.valor_liquido_cliente)
      : charged - partner - mp - xvision;

  return { partner, mp, mpPending, xvision, contractual, surcharge, totalFees, charged, net };
}

function BreakdownLine({ label, value, emphasize = false, muted = false }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2} sx={{ minWidth: 268 }}>
      <Typography
        variant="body2"
        sx={{
          color: muted ? "text.disabled" : "text.secondary",
          fontSize: "0.8125rem",
          lineHeight: 1.4,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: emphasize ? 700 : 500,
          color: muted ? "text.disabled" : "text.primary",
          fontSize: "0.8125rem",
          lineHeight: 1.4,
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="subtitle2"
      fontWeight={700}
      sx={{ color: "text.primary", mb: 0.75, fontSize: "0.8125rem" }}
    >
      {children}
    </Typography>
  );
}

function ValueBreakdownPanel({ row, t, tenantView = false }) {
  const charged = num(row.valor_cobrado);
  const { partner, mp, mpPending, xvision, contractual, surcharge, totalFees, net } = feeParts(row, {
    tenantView,
  });
  const contractFee = tenantView ? contractual : xvision;
  const showComposition = hasComposition(row);
  const mpPayment = resolveMpPaymentFromRow(row);
  const paymentLabel =
    mpPayment?.group === "mp_pending"
      ? mpPaymentTypeLabel("mp_pending", undefined, t)
      : mpPayment?.label ||
        row.forma_pagamento_nome ||
        paymentRailLabel(row.payment_rail) ||
        "—";
  const installmentsLabel = formatInstallmentsBreakdownLabel(mpPayment?.installments, t);
  const visibleFees = tenantView
    ? contractFee + surcharge
    : partner + mp + xvision + surcharge;

  return (
    <Box sx={{ p: 1.5 }}>
      <SectionTitle>{t("passagensPage.valueBreakdownPayment")}</SectionTitle>
      <Stack spacing={0.35} sx={{ mb: 1.25 }}>
        <BreakdownLine label={t("passagensPage.colPayment")} value={paymentLabel} />
        {installmentsLabel ? (
          <BreakdownLine label={t("passagensPage.valueBreakdownInstallments")} value={installmentsLabel} />
        ) : null}
      </Stack>
      <Box sx={{ borderTop: 1, borderColor: "divider", mb: 1.25 }} />

      {showComposition ? (
        <>
          <SectionTitle>{t("passagensPage.valueBreakdownComposition")}</SectionTitle>
          <Stack spacing={0.35} sx={{ mb: 1.25 }}>
            {num(row.valor_tarifa) > 0 ? (
              <BreakdownLine label={t("passagensPage.valueBreakdownTariff")} value={fmtMoney(row.valor_tarifa)} />
            ) : null}
            {num(row.valor_taxas_embarque) > 0 ? (
              <BreakdownLine
                label={t("passagensPage.valueBreakdownBoarding")}
                value={fmtMoney(row.valor_taxas_embarque)}
              />
            ) : null}
            {num(row.acrescimo_fp) > 0 ? (
              <BreakdownLine
                label={t("passagensPage.valueBreakdownSurchargeFp")}
                value={fmtMoney(row.acrescimo_fp)}
              />
            ) : null}
            {num(row.desconto_fp) > 0 ? (
              <BreakdownLine
                label={t("passagensPage.valueBreakdownDiscountFp")}
                value={`−${fmtMoney(row.desconto_fp)}`}
              />
            ) : null}
            <BreakdownLine
              label={t("passagensPage.valueBreakdownCharged")}
              value={fmtMoney(charged)}
              emphasize
            />
          </Stack>
          <Box sx={{ borderTop: 1, borderColor: "divider", mb: 1.25 }} />
        </>
      ) : null}

      {(visibleFees > 0 || !tenantView) ? (
        <>
          <SectionTitle>{t("passagensPage.valueBreakdownFees")}</SectionTitle>
          <Stack spacing={0.35}>
            {!tenantView ? (
              <>
                <BreakdownLine
                  label={t("platformPage.passengerFeePartner")}
                  value={partner > 0 ? `−${fmtMoney(partner)}` : fmtMoney(0)}
                  muted={partner <= 0}
                />
                <BreakdownLine
                  label={t("platformPage.passengerFeeMp")}
                  value={mpPending ? t("passagensPage.mpFeePending") : mp > 0 ? `−${fmtMoney(mp)}` : fmtMoney(0)}
                  muted={!mpPending && mp <= 0}
                />
              </>
            ) : null}
            <BreakdownLine
              label={
                tenantView
                  ? t("passagensPage.feeContractual", { defaultValue: "Taxa contratual" })
                  : t("platformPage.passengerFeeXvision")
              }
              value={contractFee > 0 ? `−${fmtMoney(contractFee)}` : fmtMoney(0)}
              muted={contractFee <= 0}
            />
            <BreakdownLine
              label={t("platformPage.passengerFeeSurcharge")}
              value={surcharge > 0 ? fmtMoney(surcharge) : fmtMoney(0)}
              muted={surcharge <= 0}
            />
            {!tenantView && totalFees > 0 ? (
              <BreakdownLine
                label={t("platformPage.passengerFeesTotal")}
                value={`−${fmtMoney(partner + mp + xvision)}`}
                emphasize
              />
            ) : null}
          </Stack>
        </>
      ) : null}

      {!tenantView ? (
        <>
          <Box sx={{ borderTop: 1, borderColor: "divider", my: 1.25 }} />
          <BreakdownLine
            label={t("passagensPage.valueBreakdownNet")}
            value={fmtMoney(net)}
            emphasize
          />
        </>
      ) : null}
      {surcharge > 0 ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, maxWidth: 300, lineHeight: 1.45 }}>
          {t("passagensPage.valueBreakdownSurchargeHint")}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Célula da tabela de vendas com valor cobrado e resumo das taxas.
 * @param {boolean} [tenantView] — workspace: oculta repasse parceiro, taxa MP e líquido.
 */
export default function PassengerSaleValueCell({ row, tenantView = false }) {
  const { t } = useTranslation("app");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { partner, mp, mpPending, xvision, contractual, surcharge, totalFees, net } = feeParts(row, {
    tenantView,
  });
  const contractFee = tenantView ? contractual : xvision;
  const charged = num(row.valor_cobrado);

  const summaryParts = useMemo(() => {
    const parts = [];
    if (!tenantView) {
      if (partner > 0) parts.push(`${t("passagensPage.valueBreakdownPartnerShort")} ${fmtMoney(partner)}`);
      if (mp > 0) parts.push(`MP ${fmtMoney(mp)}`);
      else if (mpPending) parts.push(`MP ${t("passagensPage.valueBreakdownMpPendingShort")}`);
    }
    if (contractFee > 0) {
      parts.push(
        tenantView
          ? `${t("passagensPage.valueBreakdownContractualShort", { defaultValue: "Contr." })} ${fmtMoney(contractFee)}`
          : `XV ${fmtMoney(contractFee)}`
      );
    }
    if (surcharge > 0) parts.push(`${t("passagensPage.valueBreakdownSurchargeShort")} ${fmtMoney(surcharge)}`);
    return parts;
  }, [contractFee, mp, mpPending, partner, surcharge, t, tenantView]);

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }
        }}
        sx={{
          textAlign: "right",
          cursor: "pointer",
          minWidth: 148,
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          mx: -0.5,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
          {fmtMoney(charged)}
        </Typography>
        {summaryParts.length > 0 ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.45, mt: 0.25 }}>
            {summaryParts.join(" · ")}
          </Typography>
        ) : totalFees <= 0 && !tenantView ? (
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25 }}>
            {t("passagensPage.valueBreakdownNoFees")}
          </Typography>
        ) : null}
        {!tenantView && net > 0 && (totalFees > 0 || row.valor_liquido_cliente != null) ? (
          <Typography
            variant="caption"
            color="success.main"
            display="block"
            sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, mt: 0.15 }}
          >
            {t("passagensPage.valueBreakdownNetShort")} {fmtMoney(net)}
          </Typography>
        ) : null}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "center", horizontal: "left" }}
        transformOrigin={{ vertical: "center", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              maxWidth: 340,
            },
          },
        }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Paper elevation={0} sx={{ bgcolor: "background.paper" }}>
          <ValueBreakdownPanel row={row} t={t} tenantView={tenantView} />
        </Paper>
      </Popover>
    </>
  );
}
